// @givmo/board — the officers' "Terms ending" panel on the Meetings page (CBG-YR3-TERMS, frame t7;
// the backend's contract `board-terms.md` §11.1, §13, call C8).
//
// The third of the page's self-hiding panels, in their shape (`carry-forward-panel.tsx`): it renders
// NOTHING when nothing has ended or ends soon, so it costs an officer no attention on an ordinary day.
// It lists, from the roster read alone and from what the board RECORDED:
//   * directors whose current term's recorded end has passed while they still serve (the approved
//     holdover words), or falls in the next three months — a display window, not a rule;
//   * officers whose title's recorded "held to" has passed or falls in the same window. No end is
//     derived here from the officers' term length (C8: the annual suggestion derives one, naming its
//     source; this panel's own words say "Terms you recorded").
// By date, never by urgency; plain words, no tone, no count, no countdown. When an annual meeting is
// scheduled, one quiet line says when, because that is where elections usually go: the meeting's own
// day in its own zone, as the meeting's pages say it. Words approved in copy round CBG-YR3-COPY1, but
// the reused holdover line, approved before.

import { useQuery } from '@tanstack/react-query';
import type { OrgBoardMeetingOut, OrgBoardMemberOut } from '@givmo/api-client';
import { Card, CardBody, FOCUS_RING } from '@givmo/design-system';
import { Link } from '../../board-routing';
import { useBoardOperations } from '../../board-runtime';
import { displayDate, todayAsDateInput } from '../../lib/display';
import { meetingDayOf } from '../in-meeting/in-meeting-decode';
import { extractBoardMembers } from '../roster/roster-decode';
import { isDirector, joinWords, keptTogether } from '../roster/officer-roles';
import { officerRolesL3Words as l3 } from '../roster/officer-roles-l3-words';
import { addMonths, currentTerm } from '../roster/terms-decode';
import { termsWords as tw } from '../roster/terms-words';

interface EndingRow {
  seatId: string;
  name: string;
  end: string;
  line: string;
}

const ended = (end: string, today: string) =>
  end < today
    ? keptTogether(l3.termEndedStillServing(displayDate(end)))
    : tw.endsOn(displayDate(end));

/** Serving directors whose current term's recorded end is on or before `until`, by end date. */
export function directorsEnding(
  seats: readonly OrgBoardMemberOut[],
  today: string,
  until: string,
): EndingRow[] {
  return seats
    .filter((seat) => seat.status === 'active' && isDirector(seat))
    .flatMap((seat) => {
      const term = currentTerm(seat);
      const end = term?.ends_on ?? null;
      if (end === null || end > until) return [];
      const said = ended(end, today);
      return [
        {
          seatId: seat.id,
          name: seat.display_name,
          end,
          line: term?.class_label ? `${said} · ${term.class_label}` : said,
        },
      ];
    })
    .sort(
      (left, right) => left.end.localeCompare(right.end) || left.name.localeCompare(right.name),
    );
}

/** Serving seats' active titles whose recorded "held to" is on or before `until`, one row per person
 *  and end, the titles named together in the board's words. */
export function officersEnding(
  seats: readonly OrgBoardMemberOut[],
  today: string,
  until: string,
): EndingRow[] {
  return seats
    .filter((seat) => seat.status === 'active')
    .flatMap((seat) => {
      const byEnd = new Map<string, string[]>();
      for (const title of seat.titles ?? []) {
        const end = title.held_to ?? null;
        if (title.status !== 'active' || end === null || end > until) continue;
        byEnd.set(end, [...(byEnd.get(end) ?? []), title.label]);
      }
      return [...byEnd.entries()].map(([end, labels]) => ({
        seatId: seat.id,
        name: `${seat.display_name}, ${joinWords(labels)}`,
        end,
        line: ended(end, today),
      }));
    })
    .sort(
      (left, right) => left.end.localeCompare(right.end) || left.name.localeCompare(right.name),
    );
}

function Group({ label, rows, testId }: { label: string; rows: EndingRow[]; testId: string }) {
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-6" data-testid={testId}>
      <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
        {label}
      </span>
      <ul className="flex flex-col">
        {rows.map((row) => (
          <li
            key={`${row.seatId}:${row.end}`}
            className="flex flex-wrap items-baseline justify-between gap-x-12 gap-y-2 border-t border-border-subtle py-8 first:border-t-0 max-sm:flex-col max-sm:items-start"
          >
            <Link
              to="/roster/$memberId"
              params={{ memberId: row.seatId }}
              className="font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline"
            >
              {row.name}
            </Link>
            <span className="font-sans text-s13 font-w400 tracking-givmo text-text-secondary">
              {row.line}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TermsEndingPanel({
  orgRef,
  annualMeeting,
}: {
  orgRef: string;
  /** The next annual meeting scheduled, if any. */
  annualMeeting: OrgBoardMeetingOut | null;
}) {
  const operations = useBoardOperations();
  const seatsQuery = useQuery({
    ...operations.officerListBoardMembersOptions({ path: { org_ref: orgRef } }),
    select: extractBoardMembers,
  });
  const seats = seatsQuery.data ?? [];
  const today = todayAsDateInput();
  const until = addMonths(today, 3) ?? today;
  const directors = directorsEnding(seats, today, until);
  const officers = officersEnding(seats, today, until);

  // Nothing ended, nothing ending: nothing to say.
  if (directors.length === 0 && officers.length === 0) return null;

  return (
    <Card testId="terms-ending-panel">
      <CardBody className="flex flex-col gap-12">
        <div className="flex flex-col gap-4">
          <h2 className="font-sans text-s16 font-w700 tracking-givmo text-text-strong">
            {tw.panelTitle}
          </h2>
          <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
            {tw.panelLine}
          </p>
        </div>
        <Group label={tw.directors} rows={directors} testId="terms-ending-directors" />
        <Group label={tw.officers} rows={officers} testId="terms-ending-officers" />
        {annualMeeting !== null ? (
          <Link
            to="/meetings/$meetingId"
            params={{ meetingId: annualMeeting.id }}
            className={`self-start rounded-sm font-sans text-s13 font-w400 tracking-givmo text-text-secondary underline decoration-border-default underline-offset-2 hover:text-text-strong ${FOCUS_RING}`}
            data-testid="terms-ending-annual"
          >
            {tw.annualMeetingOn(
              displayDate(meetingDayOf(annualMeeting)?.day ?? annualMeeting.starts_at),
            )}
          </Link>
        ) : null}
      </CardBody>
    </Card>
  );
}
